// one small gradient per background surface; only its transform and opacity animate.
const motionQuery = window.matchMedia(
    '(min-width: 1024px) and (hover: hover) and (pointer: fine) and (prefers-reduced-motion: no-preference)'
);
let disposeGlow = () => {};

function initializeGlow() {
    disposeGlow();

    const panel = document.querySelector('body:is(.fi-panel-admin, .fi-panel-conversations, .fi-panel-reports)');
    const pane = panel?.querySelector('.fi-main-ctn');

    if (!motionQuery.matches || !pane) {
        return;
    }

    const controller = new AbortController();
    const options = { passive: true, signal: controller.signal };
    const surfaces = [panel, pane, panel.querySelector('.fi-topbar')].filter(Boolean).map(host => {
        // the topbar is a Livewire component: its server-rendered, ignored layer
        // survives morphs. the body and content pane live outside those components.
        const existingLayer = host.querySelector(':scope > .nx-cursor-glow-layer--topbar');
        const layer = existingLayer ?? document.createElement('div');
        const glow = existingLayer?.querySelector('.nx-cursor-glow') ?? document.createElement('div');

        if (!existingLayer) {
            layer.className = 'nx-cursor-glow-layer';
            layer.setAttribute('aria-hidden', 'true');
            glow.className = 'nx-cursor-glow';
            layer.append(glow);
            host.classList.add('nx-cursor-glow-host');
            host.prepend(layer);
        }

        return { host, layer, glow, owned: !existingLayer, bounds: null };
    });

    let frame = null;
    let previous_time = null;
    let bounds_dirty = true;
    let visible = false;
    let target_x = 0;
    let target_y = 0;
    let current_x = 0;
    let current_y = 0;

    function draw(time) {
        frame = null;

        // geometry is read only on entry, scroll or resize, before any style writes.
        if (bounds_dirty) {
            surfaces.forEach(surface => {
                surface.bounds = surface.layer.getBoundingClientRect();
            });
            bounds_dirty = false;
        }

        const elapsed = previous_time === null ? 16 : Math.min(time - previous_time, 50);
        const blend = 1 - Math.exp(-elapsed / 110);
        current_x += (target_x - current_x) * blend;
        current_y += (target_y - current_y) * blend;

        surfaces.forEach(({ glow, bounds }) => {
            glow.style.transform = `translate3d(calc(${current_x - bounds.left}px - 50%), calc(${current_y - bounds.top}px - 50%), 0)`;
            glow.classList.add('nx-cursor-glow-visible');
        });

        if (Math.abs(target_x - current_x) + Math.abs(target_y - current_y) > 0.2) {
            previous_time = time;
            frame = requestAnimationFrame(draw);
        } else {
            previous_time = null;
        }
    }

    function scheduleDraw() {
        if (visible && frame === null) {
            frame = requestAnimationFrame(draw);
        }
    }

    function hideGlow() {
        visible = false;
        cancelAnimationFrame(frame);
        frame = null;
        previous_time = null;
        surfaces.forEach(({ glow }) => glow.classList.remove('nx-cursor-glow-visible'));
    }

    function invalidateBounds() {
        bounds_dirty = true;
        scheduleDraw();
    }

    document.addEventListener(
        'pointermove',
        event => {
            if (event.pointerType !== 'mouse' || document.hidden) {
                return;
            }

            target_x = event.clientX;
            target_y = event.clientY;

            if (!visible) {
                current_x = target_x;
                current_y = target_y;
                bounds_dirty = true;
                visible = true;
            }

            scheduleDraw();
        },
        options
    );
    document.addEventListener(
        'pointerout',
        event => {
            if (!event.relatedTarget) {
                hideGlow();
            }
        },
        options
    );
    document.addEventListener(
        'visibilitychange',
        () => {
            if (document.hidden) {
                hideGlow();
            }
        },
        options
    );
    document.addEventListener('scroll', invalidateBounds, { ...options, capture: true });
    window.addEventListener('resize', invalidateBounds, options);
    window.addEventListener('blur', hideGlow, options);

    const resizeObserver = new ResizeObserver(invalidateBounds);
    surfaces.forEach(({ host }) => resizeObserver.observe(host));

    disposeGlow = () => {
        hideGlow();
        controller.abort();
        resizeObserver.disconnect();
        surfaces.forEach(({ host, layer, glow, owned }) => {
            if (owned) {
                layer.remove();
                host.classList.remove('nx-cursor-glow-host');
            } else {
                glow.style.removeProperty('transform');
            }
        });
        disposeGlow = () => {};
    };
}

// SPA visits replace the panel DOM; release the previous listeners and layers first.
document.addEventListener('livewire:navigating', () => disposeGlow());
document.addEventListener('livewire:navigated', initializeGlow);
motionQuery.addEventListener('change', initializeGlow);

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeGlow, { once: true });
} else {
    initializeGlow();
}
